import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Form, Image } from "semantic-ui-react";

// Hooks and Helpers
import useUnresolvedDisputeForm from "@common/components/hooks/useUnresolvedDisputeForm";
import {
  getCurrentDateTime,
  setComplianceFormDateTime,
} from "../../helpers/datetime";
import useLogin from "@common/components/hooks/useLogin";

// Components
import ToggleComponent from "@common/components/shared/forms/ToggleComponent";
import FormButtonsComponent from "@common/components/shared/forms/FormButtonsComponent";
import SelectComponent from "@common/components/shared/forms/SelectComponent";
import TextareaComponent from "@common/components/shared/forms/TextareaComponent";
import SignatureComponent from "@common/components/shared/forms/SignatureComponent";
import DateAndTimeGroup from "@common/components/shared/forms/DateAndTimeGroup";
import InputComponent from "@common/components/shared/forms/InputComponent";
import UploadComponent from "@common/components/shared/forms/UploadComponent";
import UnresolvedDisputeAction from "@common/redux/actions/UnresolvedDisputeAction";
import StandardHiddenInputs from "../forms/StandardHiddenInputs";
import CustomerForm from "../CustomerForm";
import useLinkedSession from "../../hooks/useLinkedSession";
import AlertsAndSessions from "../AlertsAndSessions";

const DisplaySignature = () => {
  const { signature } = useSelector((state) =>
    UnresolvedDisputeAction.getStatePath(state),
  );

  return (
    <div>
      <b className="mb-1">Signature:</b>
      <Image className="p-1" src={signature} bordered />
    </div>
  );
};

const UnresolvedDisputeForm = ({ sigPad }) => {
  const [{ isDesktop, isInReadMode, optionDisplaySessionsInCompliance }] =
    useLogin();

  const {
    types,
    methods,
    resetForm,
    onSubmit,
    onSelect,
    handleNewCustomer,
    handleEditCustomer,
    handleRemoveCustomer,
    handleExistingCustomer,
  } = useUnresolvedDisputeForm(sigPad);
  const { setState } = useLinkedSession();

  const {
    handleSubmit,
    control,
    register,
    setValue,
    watch,
    formState: { isValid, errors },
  } = methods;

  const watchCctv = watch("cctvCatched");

  useEffect(() => {
    if (!isDesktop) {
      setComplianceFormDateTime(setValue);
    }
    setState({ name: "machine" });
  }, []);

  return (
    <Form className="form-padding" onSubmit={handleSubmit(onSubmit)}>
      <StandardHiddenInputs />
      <DateAndTimeGroup
        control={control}
        dateFormat="YYYY-MM-DD"
        errorsTime={errors.time}
        maxDate={getCurrentDateTime()}
      />
      <SelectComponent
        name="type"
        label="Type"
        placeholder="Select type of gambling dispute"
        control={control}
        error={errors.type}
        options={types}
      />
      {optionDisplaySessionsInCompliance ? (
        <AlertsAndSessions />
      ) : (
        <InputComponent
          label="Machine"
          name="machineReference"
          placeholder="Please enter the machine details [Asset No & Game Name]"
          register={register}
          error={errors.machineReference}
        />
      )}
      <TextareaComponent
        name="description"
        label="Description"
        placeholder="Enter details of the gambling dispute, including witness"
        register={register}
        error={errors.description}
      />
      <ToggleComponent
        control={control}
        name="cctvCatched"
        label="Captured by CCTV?"
        error={errors.cctvCatched}
      />
      {isDesktop && watchCctv && <UploadComponent />}
      <CustomerForm
        onSelect={onSelect}
        handleNewCustomer={handleNewCustomer}
        handleExists={handleExistingCustomer}
        handleEditCustomer={handleEditCustomer}
        handleRemoveCustomer={handleRemoveCustomer}
        handleResetCustomer={UnresolvedDisputeAction.reset}
        showCustomerUnknownCheckbox={false}
        showContacts={true}
      />
      <TextareaComponent
        name="comments"
        label="Comments"
        placeholder="Please enter comments..."
        register={register}
        fieldStyle="py-1"
      />
      {isDesktop || isInReadMode ? (
        <DisplaySignature />
      ) : (
        <SignatureComponent sigRef={sigPad} control={control} errors={errors} />
      )}
      <FormButtonsComponent
        isValid={isValid}
        resetForm={() => resetForm(false)}
      />
    </Form>
  );
};

export default UnresolvedDisputeForm;
